import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState(null);

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const savedToken = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${savedToken}` }
      });
      setUser(response.data.user);
      // Fetch role permissions after getting user (pass token explicitly)
      await fetchRolePermissions(response.data.user.role, savedToken);
    } catch (error) {
      console.error('Error fetching current user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const fetchRolePermissions = async (roleName, authToken = null) => {
    try {
      // Use my-permissions endpoint which is accessible to all authenticated users
      // Pass token explicitly to avoid race conditions with axios defaults
      const tokenToUse = authToken || token || localStorage.getItem('token');
      const response = await axios.get(`${API}/roles/my-permissions`, {
        headers: { Authorization: `Bearer ${tokenToUse}` }
      });
      const data = response.data;
      
      console.log('Fetched permissions for user:', roleName, data);
      
      if (data && data.modules) {
        console.log('Role modules:', data.modules);
        setPermissions(data.modules);
      } else {
        console.log('No modules found for role:', roleName);
        // Default: Admin gets all, others get limited
        if (roleName === 'Admin') {
          setPermissions({ _isAdmin: true });
        } else {
          setPermissions({});
        }
      }
    } catch (error) {
      console.error('Error fetching role permissions:', error);
      console.error('Error details:', error.response?.data || error.message);
      // Default permissions based on role name
      if (roleName === 'Admin') {
        setPermissions({ _isAdmin: true });
      } else {
        // Don't set empty - leave as null so we can retry
        setPermissions(null);
      }
    }
  };

  // Check if user has permission for a specific module/action
  const hasPermission = (moduleId, action = 'view') => {
    // Admin always has all permissions (check by role name first)
    if (user?.role === 'Admin') return true;
    
    // If permissions not loaded yet, return false
    if (!permissions || Object.keys(permissions).length === 0) {
      console.log('hasPermission: permissions not loaded yet for', moduleId);
      return false;
    }
    
    // Admin flag in permissions
    if (permissions._isAdmin) return true;
    
    // Check specific module permission (direct match)
    const modulePerm = permissions[moduleId];
    if (modulePerm && modulePerm[action]) return true;
    
    return false;
  };

  // Check if user can access any submodule within a parent module
  const hasAnyPermissionInModule = (submoduleIds) => {
    // Admin always has all permissions
    if (user?.role === 'Admin') return true;
    
    if (!permissions || Object.keys(permissions).length === 0) return false;
    if (permissions._isAdmin) return true;
    
    // Check direct submodule permissions only
    return submoduleIds.some(id => {
      const perm = permissions[id];
      return perm && perm.view;
    });
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const { access_token, user } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(user);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Fetch permissions for the logged-in user (pass token explicitly)
      await fetchRolePermissions(user.role, access_token);
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || error.message || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setPermissions(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const value = {
    user,
    token,
    login,
    logout,
    loading,
    isAuthenticated: !!token && !!user,
    permissions,
    hasPermission,
    hasAnyPermissionInModule,
    refreshPermissions: () => user && fetchRolePermissions(user.role)
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
